import { useContext, useEffect, useState } from 'react'

import { useQuery } from '@tanstack/react-query'

import { fetchAccountContactActivity, fetchAccountContactCount } from 'api/AccountActivityAPI'
import { SortContext } from 'context/sortContext'
import { AccountActivitySortValue, ContactWithActivites } from 'modules/accountActivity/domain'

export const useAccountContactActivity = (
  accountId: string,
  limit: number,
  pageNum: number,
  sort?: AccountActivitySortValue
): {
  response: ContactWithActivites[] | undefined
  count: number | undefined
  accountContactsError: Error | undefined
  isLoading: boolean
} => {
  const [accountContactsError, setError] = useState<Error | undefined>(undefined)

  const { contactsSort, contactsDescending } = useContext(SortContext)

  const { data: response, error: activityError, isLoading: isLoadingAccountContacts } = useQuery<
    ContactWithActivites[],
    Error
  >({
    queryKey: [
      'fetchAccountContactActivity',
      accountId,
      contactsSort,
      contactsDescending,
      limit,
      pageNum
    ],
    queryFn: () =>
      fetchAccountContactActivity({
        accountId,
        sortBy: sort ?? contactsSort,
        descending: contactsDescending,
        limit,
        pageNum
      }).then(data =>
        data.map(contact => ({
          ...contact,
          contactName: contact.endUserData.name || contact.endUserData.email || 'Unknown contact'
        }))
      ),
    staleTime: 1000 * 60
  })

  const { data: count, error: countError, isLoading: isLoadingCount } = useQuery<number, Error>({
    queryKey: ['fetchAccountContactCount', accountId],
    queryFn: () => fetchAccountContactCount(accountId).then(resp => resp.data.length),
    staleTime: 1000 * 60
  })

  useEffect(() => {
    if (activityError) {
      setError(activityError)
    }
    if (countError) {
      setError(countError)
    }
  }, [activityError, countError])

  return {
    response,
    count,
    accountContactsError,
    isLoading: isLoadingAccountContacts || isLoadingCount
  }
}
